import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useJoinLuxPlusFreePreview from 'luxPlus/hooks/useJoinLuxPlusFreePreview'
import Pill from 'LuxKit/Pill'

function LuxPlusFreePreviewModal() {
  const intl = useIntl()
  const { resolve } = useModalElementContext<boolean>()

  const closeModal = useCallback(() => resolve(true), [resolve])

  const {
    onPendingJoinFreePreview,
    hasInviteExpiryDate,
    invitePillExpiryCopy,
    formattedExpiryDate,
  } = useJoinLuxPlusFreePreview()

  const handleJoinFreePreview = useCallback(() => {
    closeModal()
    onPendingJoinFreePreview()
  }, [closeModal, onPendingJoinFreePreview])

  const descriptionWithExpiry = <FormattedMessage
    id="8r0HCJWkD65k5qA7Vlim"
    defaultMessage="Discover hidden offers, view member pricing and find out what life is like in our VIP Travel Club. <bold>No credit card required!</bold> This exclusive invite is only valid until {date}, so be sure to accept this offer before time runs out."
    description="Free preview benefits description when invite has a specific expiry date"
    values={{
      bold: (chunks) => <b>{chunks}</b>,
      date: formattedExpiryDate,
    }}
  />
  const descriptionWithoutExpiry = <FormattedMessage
    id="-RIei9uD_9SJt6WI7CcW"
    defaultMessage="Discover hidden offers, view member pricing and find out what life is like in our VIP Travel Club. <bold>No credit card required!</bold> This exclusive invite is valid for a limited period only, so be sure to accept this offer before time runs out."
    description="Free preview benefits description when invite has no specific expiry date"
    values={{
      bold: (chunks) => <b>{chunks}</b>,
    }}
  />

  return <Modal
    title={intl.formatMessage({
      id: 'IsRG-KMpXYUbZoXWzs3B',
      defaultMessage: 'LuxPlus+ {duration}-day Free Preview',
      description: 'Modal title for LuxPlus+ free preview duration offer',
    }, { duration: LUXURY_PLUS.FREE_PREVIEW_DURATION })}
    primaryActionText={intl.formatMessage({
      id: '1Oi061y8XCwyXR3Pefl_',
      defaultMessage: 'Start Free Preview now',
      description: 'Primary action button text to start LuxPlus+ free preview',
    })}
    onPrimaryActionClick={handleJoinFreePreview}
  >
    <VerticalSpacer gap={20}>
      <Pill
        endIcon={null}
        kind="primary"
        startIcon={null}
        variant="success"
      >
        {invitePillExpiryCopy}
      </Pill>
      <BodyText variant="medium" colour="light-foreground-alt">
        {hasInviteExpiryDate ? descriptionWithExpiry : descriptionWithoutExpiry}
      </BodyText>
      <BodyText variant="small" colour="light-foreground-alt">
        <FormattedMessage
          id="UiBAVzbzJvuizSgj1n_D"
          defaultMessage="To book an offer with {programName} benefits during your Free Preview you will be prompted to become a member. <link>T&Cs apply</link>"
          description="Disclaimer about booking with LuxPlus+ benefits during free preview with T&Cs link"
          values={{
            programName: LUXURY_PLUS.PROGRAM_NAME,
            link: (chunks) => <TextLink weight="regular" target="_blank" to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`}>{chunks}</TextLink>,
          }}
        />
      </BodyText>
    </VerticalSpacer>
  </Modal>
}

export default LuxPlusFreePreviewModal
